import type { BillingCoupon, BillingSubscription } from './index';
import { BillingSystem } from './types';

/**
 * The two cohorts of the Startup program. Year one gets the Startup discount, then year two
 * graduates to Elevate.
 *
 * The coupon is the only thing that tells the cohorts apart. Ordway holds both of them on the same
 * $425 plan and takes the difference off with the coupon, so their subscriptions are identical
 * down to the product id and the feature groups.
 */
export const StartupCohort = {
  Startup: 'startup',
  Elevate: 'elevate',
} as const;
export type StartupCohort = (typeof StartupCohort)[keyof typeof StartupCohort];

export interface StartupCohortDetails {
  cohort: StartupCohort;
  /** What the account pays a month with the program discount, in cents. */
  monthlyPriceCents: number;
}

const STARTUP: StartupCohortDetails = { cohort: StartupCohort.Startup, monthlyPriceCents: 0 };

const ELEVATE: StartupCohortDetails = { cohort: StartupCohort.Elevate, monthlyPriceCents: 19900 };

/**
 * Each cohort against every identifier its coupon carries: the Ordway coupon code, and the Ordway
 * coupon id. The two namespaces cannot collide, so one map holds both.
 *
 * The prices are stated here because nothing the app holds states them for both cohorts. Ordway
 * lists the plan at $425 whichever cohort holds it, the plan card prices a Startup plan at $0, and
 * the coupon that carries the difference arrives only when the request asks for coupons.
 */
const COHORT_BY_IDENTIFIER = new Map<string, StartupCohortDetails>([
  ['CIOSTARTUP', STARTUP],
  ['CPN-00017', STARTUP],
  ['CIOSTARTUPELEVATE', ELEVATE],
  ['CPN-00056', ELEVATE],
]);

function cohortFor(identifier: string | undefined | null): StartupCohortDetails | null {
  if (identifier == null) {
    return null;
  }
  // Exact matches only. `CIOSTARTUPELEVATE` also starts with `CIOSTARTUP`, so a prefix match puts
  // the Elevate cohort in the wrong year.
  return COHORT_BY_IDENTIFIER.get(identifier.trim().toUpperCase()) ?? null;
}

/**
 * The Startup-program cohort the account is in, or null for every other coupon.
 *
 * The subscription's `coupon_code` is read first. The billing profile always carries it, but it
 * carries the coupon object only when the request asks for coupons.
 */
export function startupCohort(
  coupon: BillingCoupon | undefined,
  subscription: BillingSubscription | undefined,
): StartupCohortDetails | null {
  if (subscription?.billingSystem === BillingSystem.Ordway) {
    const fromSubscription = cohortFor(subscription.coupon_code);
    if (fromSubscription != null) {
      return fromSubscription;
    }
  }

  if (coupon == null) {
    return null;
  }

  // Only the Ordway coupon model carries a code.
  if ('code' in coupon) {
    const fromCode = cohortFor(coupon.code);
    if (fromCode != null) {
      return fromCode;
    }
  }

  return cohortFor(coupon.id);
}
